import {
  isDocRef,
  isObject,
  TypeGuard,
  type DocumentReference,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { type IAppointment } from '../appointment/appointment';
import { type IAppointmentRequest } from '../appointment/appointment-request';
import { type IEvent } from '../event/event';
import { type IPractice } from '../practice/practice';
import { type IPatient } from '../patient/patient';
import { type DepositCaptureStrategy } from './deposit-capture-strategy';
import { type DepositPaymentMode } from './deposit-payment-mode';
import { type IDepositPaymentAttempt } from './deposit-payment-attempt';
import {
  type DepositCaptureStrategyFallbackReason,
  type DepositRequestDeliveryChannel,
  type DepositRequestOutcome,
  type IDepositRequest,
} from './deposit-request';

/**
 * Request and response contracts of the deposit callables. Organisation and
 * acting staffer are deliberately absent: the server derives both from the
 * practice and the caller, so a client cannot nominate them.
 */
export interface ICreateDepositRequestCommonParams {
  practiceRef: DocumentReference<IPractice>;
  patientRef: DocumentReference<IPatient>;
  paymentMode: DepositPaymentMode;
  captureStrategy?: DepositCaptureStrategy;
  deliveryChannel?: DepositRequestDeliveryChannel;
}

export interface ICreateDepositRequestParams
  extends ICreateDepositRequestCommonParams {
  appointmentRef?: DocumentReference<IAppointment>;
  appointmentRequestRef?: DocumentReference<IAppointmentRequest>;
  depositRequestId?: string;
  treatmentName?: string;
  amount?: number;
  amountCents?: number;
  fallbackReason?: DepositCaptureStrategyFallbackReason;
  appointmentDate?: string;
  appointmentTime?: string;
  patientEmail?: string;
  patientFirstName?: string;
  patientName?: string;
  practiceName?: string;
  event?: IEvent;
  templateId?: string;
  requestKey?: string;
}

export interface ICreateDepositRequestResponse {
  depositRequestId: string;
  depositRequestRef: DocumentReference<IDepositRequest>;
  appointmentRequestRef?: DocumentReference<IAppointmentRequest>;
  paymentUrl: string;
  deliveryStatus: DepositRequestOutcome;
  deliveryError?: string;
}

export interface ISupersedeDepositRequestParams
  extends ICreateDepositRequestParams {
  appointmentRequestRef: DocumentReference<IAppointmentRequest>;
  depositRequestId: string;
}

export interface ICreateManualDepositRequestRequest {
  practiceRef: DocumentReference<IPractice>;
  appointmentRef: DocumentReference<IAppointment>;
}

export interface ICancelDepositRequestForManualRecordRequest {
  practiceRef: DocumentReference<IPractice>;
  appointmentRef: DocumentReference<IAppointment>;
}

export interface IReleaseDepositHoldRequest {
  readonly appointmentRef: DocumentReference<IAppointment>;
}

export const isReleaseDepositHoldRequest =
  TypeGuard.interface<IReleaseDepositHoldRequest>({
    appointmentRef: isDocRef,
  });

interface IWaiveDepositRequestBase {
  readonly waiverReason: string;
}

export type IWaiveDepositRequest = IWaiveDepositRequestBase &
  (
    | {
        readonly appointmentRef: DocumentReference<IAppointment>;
        readonly appointmentRequestRef?: never;
      }
    | {
        readonly appointmentRef?: never;
        readonly appointmentRequestRef: DocumentReference<IAppointmentRequest>;
      }
  );

const isWaiveDepositRequestBase = TypeGuard.interface<IWaiveDepositRequestBase>(
  { waiverReason: isString }
);

export function isWaiveDepositRequest(
  data: unknown
): data is IWaiveDepositRequest {
  return (
    isObject(data) &&
    isWaiveDepositRequestBase(data) &&
    isDocRef(data.appointmentRef) !== isDocRef(data.appointmentRequestRef)
  );
}

export interface IApproveBookingRequestDepositRequest {
  readonly appointmentRef: DocumentReference<IAppointment>;
  readonly appointmentRequestRef: DocumentReference<IAppointmentRequest>;
}

export const isApproveBookingRequestDepositRequest =
  TypeGuard.interface<IApproveBookingRequestDepositRequest>({
    appointmentRef: isDocRef,
    appointmentRequestRef: isDocRef,
  });

export interface ICheckBookingRequestDepositRequest {
  readonly appointmentRequestRef: DocumentReference<IAppointmentRequest>;
}

export const isCheckBookingRequestDepositRequest =
  TypeGuard.interface<ICheckBookingRequestDepositRequest>({
    appointmentRequestRef: isDocRef,
  });

/** Set on a deposit callable's `HttpsError.details.code` for the client to act on. */
export enum DepositCallableErrorCode {
  AuthorisationLapsed = 'deposit-authorisation-lapsed',
}

export function isDepositAuthorisationLapsedError(error: unknown): boolean {
  return (
    isObject(error) &&
    isObject(error.details) &&
    error.details.code === DepositCallableErrorCode.AuthorisationLapsed
  );
}

export interface IRefundDepositRequest {
  paymentAttemptRef: DocumentReference<IDepositPaymentAttempt>;
  reason: string;
}

export const isRefundDepositRequest =
  TypeGuard.interface<IRefundDepositRequest>({
    paymentAttemptRef: isDocRef,
    reason: isString,
  });

export enum RefundDepositAction {
  Cancelled = 'cancelled',
  Refunded = 'refunded',
}

export interface IRefundDepositResponse {
  success: boolean;
  action: RefundDepositAction;
}
